import React, { Dispatch, ReactNode, SetStateAction } from "react";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import IDHFormattedMessage from "../IDHFormattedMessage/IDHFormattedMessage";
import moment from "moment";
import { CustomInputLabel } from "../CustomInput/CustomInput";
import { translateMessage } from "src/app/methods/translateMessage";
import { useIntl } from "react-intl";
import { addMonths } from "date-fns";

import "./DateRangeTwoSelectors.scss";

interface Props {
  dateFrom: string | null;
  setDateFrom: Dispatch<SetStateAction<string | null>>;
  dateTo: string | null;
  setDateTo: Dispatch<SetStateAction<string | null>>;
  minDateFrom?: Date;
}

export const DateRangeTwoSelectors = (props: Props) => {
  const { dateFrom, setDateFrom, dateTo, setDateTo, minDateFrom } = props;

  const intl = useIntl();

  const minDate = dateFrom ? new Date(dateFrom) : undefined;
  const maxDate = dateTo ? new Date(dateTo) : undefined;

  const dateFromLabels = [
    {
      value: -1,
      label: (
        <IDHFormattedMessage id="ws_last_month" defaultMessage="Last month" />
      ),
    },
    {
      value: -3,
      label: (
        <IDHFormattedMessage
          id="ws_last_3_months"
          defaultMessage="Last 3 months"
        />
      ),
    },
    {
      value: -6,
      label: (
        <IDHFormattedMessage
          id="ws_last_6_months"
          defaultMessage="Last 6 months"
        />
      ),
    },
    {
      value: -12,
      label: (
        <IDHFormattedMessage id="ws_last_year" defaultMessage="Last year" />
      ),
    },
  ];

  const dateToLabels = [
    {
      value: 1,
      label: (
        <IDHFormattedMessage id="ws_next_month" defaultMessage="Next month" />
      ),
    },
    {
      value: 2,
      label: (
        <IDHFormattedMessage
          id="ws_next_2_months"
          defaultMessage="Next 2 months"
        />
      ),
    },
    {
      value: 3,
      label: (
        <IDHFormattedMessage
          id="ws_next_3_months"
          defaultMessage="Next 3 months"
        />
      ),
    },
  ];

  const updateDate = (numberOfMonths: number) => {
    const updatedValue = moment(addMonths(new Date(), numberOfMonths)).format(
      "YYYY-MM-DD",
    );

    if (numberOfMonths > 0) {
      setDateTo(updatedValue);
    } else {
      setDateFrom(updatedValue);
    }
  };

  const handleChangeDateFrom = (newValue: string) => {
    setDateFrom(moment(newValue).format("YYYY-MM-DD"));
  };

  const handleChangeDateTo = (newValue: string) => {
    setDateTo(moment(newValue).format("YYYY-MM-DD"));
  };

  const renderFooter = (labels: { value: number; label: ReactNode }[]) => (
    <div className="date-range-two-selectors__footer">
      <div className="date-range-two-selectors__footer-header">
        <IDHFormattedMessage
          id="ws_suggested_date"
          defaultMessage="Suggested date"
        />
      </div>
      <div className="date-range-two-selectors__footer-labels">
        {labels.map(({ value, label }) => (
          <span
            className="date-range-two-selectors__footer-labels-item"
            onClick={() => updateDate(value)}
          >
            {label}&nbsp;+
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <div className="date-range-two-selectors">
      <div className="date-range-two-selectors__wrapper">
        <CustomInputLabel>
          <IDHFormattedMessage id="ws_date_from" defaultMessage="Date from" />
        </CustomInputLabel>
        <CustomDatePicker
          key={dateFrom}
          initialDate={dateFrom}
          dateChangeHandler={handleChangeDateFrom}
          minDate={minDateFrom}
          maxDate={maxDate}
          disableScrollLock
          footer={renderFooter(dateFromLabels)}
          placeholder={translateMessage({
            intl,
            id: "ws_date_from_placeholder",
            defaultMessage: "Select date from",
          })}
        />
      </div>
      <div className="date-range-two-selectors__wrapper">
        <CustomInputLabel>
          <IDHFormattedMessage id="ws_date_to" defaultMessage="Date to" />
        </CustomInputLabel>
        <CustomDatePicker
          key={dateTo}
          initialDate={dateTo}
          dateChangeHandler={handleChangeDateTo}
          minDate={minDate}
          disableScrollLock
          footer={renderFooter(dateToLabels)}
          placeholder={translateMessage({
            intl,
            id: "ws_date_to_placeholder",
            defaultMessage: "Select date to",
          })}
        />
      </div>
    </div>
  );
};
